import { Performer, PerformerAvatar, IPerformerServices } from './performer.model';
import { IUrlParams, UrlQuery } from '../core/params';
import { User } from '../core/models/user';
import fetch from '../core/fetch-extension';
import transformPerformer from '../performer/performer.transformer';
import config from '../core/config';
import { ApiError } from '../core/errors';
import { Result, Ok, Err } from '../core/result';
import { ClientReview } from '../core/models/review';

export interface PerformerList {
    performerAccounts: Performer[];
    total: number;
}

export interface PerformerResult {
    performerAccount: Performer;
    photos: PhotoGroup;
    medias: MediaGroup;
    reviews: ReviewGroup;
}

export interface PhotoGroup {
    approved?: { photos: PerformerAvatar[], total: number; };
    pending?: { photos: PerformerAvatar[], total: number; };
    rejected?: { photos: PerformerAvatar[], total: number; };
}

export interface MediaGroup {
    approved?: { medias: PerformerAvatar[], total: number; };
    pending?: { medias: PerformerAvatar[], total: number; };
    rejected?: { medias: PerformerAvatar[], total: number; };
}

export interface ReviewGroup {
    reviewable: boolean;
    total: number;
    reviews: ClientReview[];
    quote?:ClientReview;
}

export interface PerformerListQuery extends UrlQuery {
    category?: string;
    withPhotos?: number;
    performer?: number;
    search?: string;
    voyeur?: number;
}

export interface SimplePerformer {
    username: string;
    id: number;
    adv: number;
    img: string;
}

export interface UsernamesListQuery {
    extra: number;
    service?: string;
}

export type PerformerListResult = Result<PerformerList, ApiError>;
export type PerformerResponse = Result<Performer, ApiError>;
export type UsernamesResult = Result<SimplePerformer[], ApiError>;

/**
 * Retrieve a list of performer accounts. This resource is searchable with a string
 *
 * @param query - Basic param field definition
 */
export async function listPerformers(query: PerformerListQuery = {
    offset: 0,
    limit: 40
}): Promise<PerformerListResult> {
    const performersResult = await fetch(`${config.ApiUrl}/performer/performer_accounts`, {
        query: query
    });

    if(!performersResult.ok){
        return Err(new ApiError(performersResult.status));
    }

    const performersData: PerformerList = await performersResult.json();

    performersData.performerAccounts = performersData.performerAccounts.map(performer => {
        return performer = transformPerformer(performer);
    });

    return Ok(performersData);
}

/**
 * An API call to retrieve a list of all performer usernames. Can be used for autocomplete purposes
 */
export async function listUsernames(query: UsernamesListQuery = {
    extra: 1,
    service: 'email'
}): Promise<UsernamesResult> {
    const usernamesResult = await fetch(`${config.ApiUrl}/performer/performer_accounts/usernames`, {
        query: query
    });

    if(!usernamesResult.ok){
        return Err(new ApiError(usernamesResult.status));
    }

    const data: SimplePerformer[] = await usernamesResult.json();

    return Ok(data);
}

/**
 * Retrieve a single performer account by id
 *
 * @param performerId - The performer id
 */
export async function get(performerId: number): Promise<PerformerResponse>{
    const performerResult = await fetch(`${config.ApiUrl}/performer/performer_accounts/${performerId}`);
    const performerData: PerformerResult = await performerResult.json();

    return Ok(transformPerformer(performerData.performerAccount));
}

/**
 * Retrieve a single performer account by AdvertId by params
 * Definition - AdvertId: The public id of a performer, which is visible and searchable by the consumer.
 *
 * @param advertId - The performer advert id
 */
export async function getByAdvert(advertId: number, query: PerformerListQuery = {
    offset: 0,
    limit: 12
}): Promise<PerformerResponse>{
    const performerResult = await fetch(`${config.ApiUrl}/performer/performer_accounts/performer_number/${advertId}`, {
        query: query
    });
    const performerData: PerformerResult = await performerResult.json();

    return Ok(transformPerformer(performerData.performerAccount, performerData.photos, performerData.medias, performerData.reviews));
}

/**
 * Retrieve recommend performers by params
 *
 * @param params - Basic param field definition
 */
export async function listRecommended(query: PerformerListQuery = {
    offset: 0,
    limit: 20
}): Promise<PerformerListResult> {
    const result = await fetch(`${config.ApiUrl}/performer/performer_accounts/recommended`, {
        query: query
    });

    if(!result.ok){
        return Err(new ApiError(result.status));
    }

    const performersData: PerformerList = await result.json();

    performersData.performerAccounts = performersData.performerAccounts.map(performer => {
        return performer = transformPerformer(performer);
    });

    return Ok(performersData);
}

/**
 * Retrieve busy performers by params
 *
 * @param params - Basic param field definition
 */
export async function listBusy(query: PerformerListQuery = {
    offset: 0,
    limit: 20
}): Promise<PerformerListResult> {
    const result = await fetch(`${config.ApiUrl}/performer/performer_accounts/busy`, {
        query: query
    });

    if(!result.ok){
        return Err(new ApiError(result.status));
    }

    const performersData: PerformerList = await result.json();

    performersData.performerAccounts = performersData.performerAccounts.map(performer => {
        return performer = transformPerformer(performer);
    });

    return Ok(performersData);
}

/**
 * Retrieve which services the performer has enabled
 *
 * @param performerId - The performer id
 */
export async function getServices(performerId: number): Promise<IPerformerServices>{

    const servicesResult = await fetch(`${config.ApiUrl}/performer/performer_account/${performerId}/performer_services`);
    const servicesData = await servicesResult.json();

    return servicesData;
}

/**
 * Retrieve all photo's from a single performer by advert id
 *
 * @param advertId - The performer advert id
 */
// getPhotos(advertId: number, photoStatus: IPerformerAvatarStatus): Promise<IPerformerAvatar[]> {

//     const photosResult = await fetch(config.ApiUrl + '/performer/performer_accounts/performer_number/' + advertId + '/photos/group');

//     a.then((res: Response) => res.json())
//         .then((res: IPhotoResult) => res[photoStatus].photos);
// }

// /**
//  * Create a new performer
//  *
//  * @param performer - The data of the performer that has to be created
//  */
// create(performer: IPerformer): Promise<IPerformer>{

//     return fetch(`${config.ApiUrl}/performer/performer_accounts`, {
//         method: 'POST',
//         body: performer
//     }).then((res: Response) => res.json())
//         .then((res: IPerformerResult) => transformPerformer(res.performerAccount));
// }

/**
 * Update the data of a performer
 *
 * @param performer - The performer resource that has to be updated
 */
export async function update(performer: Performer): Promise<PerformerResponse> {
    const result = await fetch(`${config.ApiUrl}/performer/performer_accounts/${performer.id}?language=all`, {
        method: 'PUT',
        body: performer
    });

    if(!result.ok){
        return Err(new ApiError(result.status));
    }

    const data = await result.json();

    return Ok(transformPerformer(data));
}

// /**
//  * Remove a performer
//  *
//  * @param performer - The data of the performer that has to be removed (Just the id field is necessary)
//  */
// remove(performer: IPerformer): Promise<IPerformer>{
//     return fetch(config.ApiUrl + '/performer/performer_accounts', {
//         method: 'DELETE',
//         body: performer
//     }).then((res: Response) => res.json())
//         .then((res: IPerformerResult) => transformPerformer(res.performerAccount));
// }


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./node_modules/sensejs/performer/index.ts